import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { ListChecks, Loader2, Sparkles, Brain, BatteryCharging, ShieldAlert, Clock, CheckCircle2, Circle, PartyPopper, Flame, ArrowRightLeft } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const PRIORITY = {
  high: "bg-persian-rose/15 text-persian-rose border-persian-rose/30",
  medium: "bg-persian-blue/10 text-navy border-persian-blue/30",
  low: "bg-slate-100 text-slate-500 border-slate-200",
};

export function DailyLedger({ context = "" }) {
  const [ledger, setLedger] = useState(null);
  const [generatedAt, setGeneratedAt] = useState(null);
  const [loading, setLoading] = useState(false);
  const [doneIdx, setDoneIdx] = useState([]);
  const [streak, setStreak] = useState(0);
  const [clearedToday, setClearedToday] = useState(false);

  useEffect(() => {
    api.get("/ledger/latest").then(({ data }) => {
      if (data.result) {
        setLedger(data.result);
        setGeneratedAt(data.generated_at);
        setDoneIdx(data.done_indices || []);
      }
      setStreak(data.streak || 0);
      setClearedToday(!!data.cleared_today);
    }).catch((err) => console.error("Failed to load latest ledger", err));
    // Load once on mount; setters are stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const generate = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/ledger/generate", { context });
      setLedger(data);
      setGeneratedAt(new Date().toISOString());
      setDoneIdx(data.done_indices || []);
      setStreak(data.streak || 0);
      setClearedToday(!!data.cleared_today);
      toast.success("Today's execution ledger is ready.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const tasks = ledger?.tackle_first || [];
  const total = tasks.length;
  const doneCount = doneIdx.filter((i) => i < total).length;
  const allDone = total > 0 && doneCount === total;

  // Keep original index for testids/numbering; push completed tasks to the bottom.
  const orderedTasks = useMemo(() => {
    const done = new Set(doneIdx);
    return tasks
      .map((t, i) => ({ t, i }))
      .sort((a, b) => (done.has(a.i) ? 1 : 0) - (done.has(b.i) ? 1 : 0));
  }, [tasks, doneIdx]);

  const toggleTask = async (index, currentlyDone) => {
    const next = currentlyDone ? doneIdx.filter((i) => i !== index) : [...doneIdx, index];
    setDoneIdx(next);
    try {
      const { data } = await api.patch("/ledger/task", { index, done: !currentlyDone });
      setDoneIdx(data.done_indices || []);
      const prevStreak = streak;
      setStreak(data.streak || 0);
      setClearedToday(!!data.cleared_today);
      if (data.cleared_today && (data.streak || 0) > prevStreak) {
        toast.success(`Cleared for today — ${data.streak}-day streak! 🔥`);
      }
    } catch (err) {
      setDoneIdx(doneIdx); // revert
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <div className="rounded-2xl bg-navy text-white p-6 mb-8 relative overflow-hidden" data-testid="daily-ledger">
      <div className="absolute -top-20 -right-20 h-56 w-56 rounded-full bg-persian-blue/15 blur-3xl" />
      <div className="relative">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <span className="text-[11px] font-mono uppercase tracking-[0.2em] text-persian-blue font-semibold">
              Daily Priority Engine
            </span>
            <h2 className="font-serif text-2xl sm:text-3xl font-semibold mt-1">Daily Execution Ledger</h2>
            {generatedAt && (
              <p className="text-xs text-slate-400 mt-1">
                Updated {new Date(generatedAt).toLocaleString()}
              </p>
            )}
          </div>
          <div className="flex items-center gap-3 shrink-0">
            {streak > 0 && (
              <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
                data-testid="ledger-streak"
                className={`flex items-center gap-1.5 rounded-full border px-3 py-1.5 ${clearedToday ? "bg-persian-rose/15 border-persian-rose/40 text-persian-rose" : "bg-white/5 border-white/15 text-slate-200"}`}>
                <Flame className={`h-4 w-4 ${clearedToday ? "text-persian-rose" : "text-slate-400"}`} />
                <span className="text-sm font-semibold">{streak}</span>
                <span className="text-[11px] uppercase tracking-wide">day{streak === 1 ? "" : "s"}</span>
              </motion.div>
            )}
            <Button onClick={generate} disabled={loading} data-testid="ledger-generate-button"
              className="bg-persian-rose hover:bg-persian-rosehover text-white font-semibold">
              {loading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Prioritising…</>
                : <><Sparkles className="h-4 w-4 mr-2" />{ledger ? "Regenerate" : "Generate today's plan"}</>}
            </Button>
          </div>
        </div>

        {!ledger && !loading && (
          <p className="text-sm text-slate-300 mt-4 max-w-2xl">
            Let the engine read your deadlines and any uploaded syllabus, then hand you a ruthless,
            burnout-proof plan for today — what to tackle first, what to study next, and how to spend
            your energy.
          </p>
        )}

        {ledger && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mt-5 grid grid-cols-1 lg:grid-cols-3 gap-5">
            <div className="lg:col-span-2">
              <div className="flex items-center justify-between gap-2 mb-2">
                <div className="flex items-center gap-2 text-persian-blue">
                  <ListChecks className="h-4 w-4" /><span className="text-sm font-semibold uppercase tracking-wide">Tackle first</span>
                </div>
                {total > 0 && (
                  <span className="text-xs font-mono text-slate-300" data-testid="ledger-progress-count">
                    {doneCount} of {total} done
                  </span>
                )}
              </div>

              {total > 0 && (
                <div className="h-1.5 w-full rounded-full bg-white/10 mb-3 overflow-hidden" data-testid="ledger-progress-bar">
                  <motion.div className="h-full rounded-full bg-persian-blue"
                    initial={false}
                    animate={{ width: `${(doneCount / total) * 100}%` }}
                    transition={{ type: "spring", stiffness: 120, damping: 20 }} />
                </div>
              )}

              <p className="text-sm text-slate-300 mb-3">{ledger.focus_summary}</p>

              <AnimatePresence>
                {allDone && (
                  <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
                    className="rounded-xl bg-persian-blue/15 border border-persian-blue/40 p-4 mb-3 flex items-center gap-3"
                    data-testid="ledger-cleared-banner">
                    <PartyPopper className="h-5 w-5 text-persian-blue shrink-0" />
                    <div>
                      <p className="text-sm font-semibold text-white">Cleared for today.</p>
                      <p className="text-xs text-slate-300">Every priority is done — protect your energy for tomorrow.</p>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              <ol className="space-y-2">
                {orderedTasks.map(({ t, i }) => {
                  const isDone = doneIdx.includes(i);
                  return (
                    <motion.li key={i} layout data-testid={`ledger-task-${i}`}
                      className={`rounded-xl border p-3.5 transition-colors ${isDone ? "bg-white/[0.03] border-white/5" : "bg-white/5 border-white/10"}`}>
                      <div className="flex items-start gap-3">
                        <button onClick={() => toggleTask(i, isDone)} data-testid={`ledger-task-toggle-${i}`}
                          aria-label={isDone ? "Mark as not done" : "Mark as done"}
                          className="mt-0.5 shrink-0 text-persian-blue hover:text-persian-rose transition-colors">
                          {isDone ? <CheckCircle2 className="h-5 w-5" /> : <Circle className="h-5 w-5 text-slate-400" />}
                        </button>
                        <div className="flex-1">
                          <div className="flex items-center justify-between gap-2">
                            <span className={`font-medium text-sm ${isDone ? "text-slate-400 line-through" : "text-white"}`}>
                              {i + 1}. {t.title}
                            </span>
                            <div className="flex items-center gap-1.5 shrink-0">
                              {t.carried_over && (
                                <span data-testid={`ledger-carried-${i}`}
                                  className={`inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide border rounded-full px-2 py-0.5 bg-persian-blue/10 text-persian-blue border-persian-blue/30 ${isDone ? "opacity-50" : ""}`}>
                                  <ArrowRightLeft className="h-2.5 w-2.5" /> Carried over
                                </span>
                              )}
                              <span className={`text-[10px] font-semibold uppercase tracking-wide border rounded-full px-2 py-0.5 ${PRIORITY[t.priority] || PRIORITY.low} ${isDone ? "opacity-50" : ""}`}>
                                {t.priority}
                              </span>
                            </div>
                          </div>
                          <p className={`text-xs mt-1 ${isDone ? "text-slate-500 line-through" : "text-slate-300"}`}>{t.reason}</p>
                          {t.estimated_minutes ? (
                            <span className={`inline-flex items-center gap-1 text-[11px] mt-1.5 ${isDone ? "text-slate-500" : "text-persian-blue"}`}>
                              <Clock className="h-3 w-3" /> ~{t.estimated_minutes} min
                            </span>
                          ) : null}
                        </div>
                      </div>
                    </motion.li>
                  );
                })}
              </ol>
            </div>

            <div className="space-y-4">
              <div className="rounded-xl bg-white/5 border border-white/10 p-4">
                <div className="flex items-center gap-2 text-persian-blue mb-2">
                  <Brain className="h-4 w-4" /><span className="text-sm font-semibold">Study next</span>
                </div>
                <ul className="space-y-1.5">
                  {(ledger.study_next || []).map((s, i) => (
                    <li key={i} className="text-sm text-slate-200">
                      <span className="text-white font-medium">{s.concept}</span>
                      <span className="block text-xs text-slate-400">{s.reason}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="rounded-xl bg-white/5 border border-white/10 p-4">
                <div className="flex items-center gap-2 text-persian-blue mb-1.5">
                  <BatteryCharging className="h-4 w-4" /><span className="text-sm font-semibold">Energy plan</span>
                </div>
                <p className="text-xs text-slate-300 leading-relaxed">{ledger.energy_plan}</p>
              </div>
              <div className="rounded-xl bg-persian-rose/10 border border-persian-rose/30 p-4">
                <div className="flex items-center gap-2 text-persian-rose mb-1.5">
                  <ShieldAlert className="h-4 w-4" /><span className="text-sm font-semibold">Burnout guard</span>
                </div>
                <p className="text-xs text-slate-200 leading-relaxed">{ledger.burnout_guard}</p>
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}
